import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ErrorLogItem } from "@/features/errorLogs/ErrorLogItem";
import { formatDateTime } from "@/lib/format";
import {
  fetchErrorLogSummary,
  fetchErrorLogs,
  fetchRealtimeStats,
  refreshErrorLogs,
  type DbPoolStats,
  type ErrorLogEntry,
  type ErrorLogSummary,
  type RealtimeStatsResponse,
} from "@/lib/errorLogsApi";

function PoolStat({ name, pool }: { name: string; pool: DbPoolStats }) {
  const used = pool.used ?? 0;
  const free = pool.free ?? 0;
  const max = pool.max;
  const utilization = max > 0 ? Math.round((used / max) * 100) : 0;
  const stressed = utilization >= 80;

  return (
    <div className="rounded-lg border border-separator bg-fill-tertiary/40 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{name}</span>
        <span className={stressed ? "text-xs font-semibold text-status-orange" : "text-xs text-label-tertiary"}>
          {utilization}%
        </span>
      </div>
      <p className="mt-1 text-sm text-label">
        {used} belegt · {free} frei · max. {max}
      </p>
    </div>
  );
}

export function AppHealthSection() {
  const [summary, setSummary] = useState<ErrorLogSummary | null>(null);
  const [errors, setErrors] = useState<ErrorLogEntry[]>([]);
  const [stats, setStats] = useState<RealtimeStatsResponse | null>(null);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAcknowledged, setShowAcknowledged] = useState(false);

  const load = useCallback(async (force = false) => {
    setError(null);
    try {
      if (force) {
        setRefreshing(true);
        await refreshErrorLogs();
      }
      const [summaryData, errorData, statsData] = await Promise.all([
        fetchErrorLogSummary(),
        fetchErrorLogs(showAcknowledged),
        fetchRealtimeStats(),
      ]);
      setSummary(summaryData);
      setErrors(errorData.errors);
      setAiEnabled(errorData.ai_enabled);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "App-Status konnte nicht geladen werden.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showAcknowledged]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      // Versteckter Tab: kein Hintergrund-Polling (Serverlast).
      if (document.visibilityState === "hidden") return;
      void load();
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [load]);

  const isHealthy = summary ? summary.open_count === 0 : false;

  return (
    <Card
      title="App-Status & Fehlerüberwachung"
      description="Server-Fehler aus den API-Logs, DB-Verbindungspools und Realtime-Verbindungen – damit du Probleme erkennst, bevor Nutzer sich melden."
      actions={
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={refreshing}
          onClick={() => void load(true)}
        >
          {refreshing ? "Aktualisiert …" : "Aktualisieren"}
        </Button>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 py-6 text-sm text-label-secondary">
          <Spinner />
          Status wird geladen …
        </div>
      ) : (
        <div className="space-y-6">
          {error ? (
            <Alert variant="error">{error}</Alert>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatusTile
              label="Gesamtstatus"
              value={
                summary?.status === "kritisch"
                  ? "Kritisch"
                  : summary?.status === "warnung"
                    ? "Warnung"
                    : summary?.status === "unkritisch"
                      ? "Unkritisch"
                      : "Stabil"
              }
              detail={isHealthy ? "Keine offenen Server-Fehler" : `${summary?.open_count ?? 0} offene Fehlertypen`}
              variant={
                summary?.status === "kritisch"
                  ? "error"
                  : summary?.status === "warnung" || summary?.status === "unkritisch"
                    ? "warning"
                    : "success"
              }
            />
            <StatusTile
              label="Offene Fehler"
              value={String(summary?.open_count ?? 0)}
              detail={
                summary
                  ? `${summary.severity_counts?.kritisch ?? 0} kritisch · ${summary.severity_counts?.warnung ?? 0} Warnung · ${summary.severity_counts?.unkritisch ?? 0} unkritisch`
                  : "Keine aktuellen Vorkommen"
              }
              variant={summary && summary.open_count > 0 ? "warning" : "success"}
            />
            <StatusTile
              label="Vorkommen"
              value={String(summary?.total_occurrences ?? 0)}
              detail={`${summary?.total_count ?? 0} Fehlertypen im Log-Zeitraum`}
              variant="info"
            />
            <StatusTile
              label="Log-Verzeichnis"
              value="API"
              detail={summary?.log_dir?.split("/").slice(-2).join("/") || "–"}
              variant="info"
            />
          </div>

          {stats ? (
            <section className="space-y-3">
              <h3 className="text-sm font-semibold text-label">Infrastruktur</h3>
              <div className="grid gap-3 sm:grid-cols-3">
                {Object.entries(stats.db_pools).map(([name, pool]) => (
                  <PoolStat key={name} name={name} pool={pool} />
                ))}
              </div>
              <p className="text-xs text-label-tertiary">
                Stand: {formatDateTime(stats.generated_at)} · Offpaper-Subscriber:{" "}
                {stats.offpaper_sync_subscribers}
                {stats.offpaper_sync_queue_overflow_count > 0
                  ? ` · Queue-Overflows: ${stats.offpaper_sync_queue_overflow_count}`
                  : ""}
              </p>
            </section>
          ) : null}

          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-label">Offene Server-Fehler</h3>
              <label className="flex items-center gap-2 text-xs text-label-secondary">
                <input
                  type="checkbox"
                  checked={showAcknowledged}
                  onChange={(event) => setShowAcknowledged(event.target.checked)}
                  className="rounded border-separator"
                />
                Erledigte einblenden
              </label>
            </div>

            {errors.length === 0 ? (
              <Alert variant="success">Keine offenen Fehler – alles ruhig.</Alert>
            ) : (
              <div className="space-y-4">
                {errors.map((entry) => (
                  <ErrorLogItem
                    key={entry.fingerprint}
                    error={entry}
                    aiEnabled={aiEnabled}
                    onChanged={() => void load()}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </Card>
  );
}

function StatusTile({
  label,
  value,
  detail,
  variant,
}: {
  label: string;
  value: string;
  detail: string;
  variant: "success" | "warning" | "error" | "info";
}) {
  const variantClass =
    variant === "success"
      ? "border-status-green/30 bg-[color-mix(in_srgb,_var(--eh-green)_10%,_var(--eh-bg-card))]"
      : variant === "warning"
        ? "border-status-orange/30 bg-[color-mix(in_srgb,_var(--eh-orange)_10%,_var(--eh-bg-card))]"
        : variant === "error"
          ? "border-status-red/30 bg-[color-mix(in_srgb,_var(--eh-red)_10%,_var(--eh-bg-card))]"
          : "border-separator bg-fill-tertiary/30";

  return (
    <div className={`rounded-lg border px-3 py-3 ${variantClass}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">{label}</p>
      <p className="mt-1 text-lg font-bold text-label">{value}</p>
      <p className="mt-0.5 text-xs text-label-tertiary">{detail}</p>
    </div>
  );
}
